import { format } from 'date-fns';
import { Edit, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { ROUTE_PATHS } from '@/config/constants';
import { SliceTags } from '@/pages/MerchandisingCollections/components/SliceTags';
import { getSliceStatus } from '@/pages/MerchandisingCollections/utils/getSliceStatus';
import { MerchandisingCollectionSlice } from '@/services/MerchandisingCollectionsService';

interface SliceCardProps {
  slice: MerchandisingCollectionSlice;
  onRemove: (slice: MerchandisingCollectionSlice) => void;
}

export function SliceCard({ slice, onRemove }: SliceCardProps) {
  const { t } = useTranslation('translation', {
    keyPrefix:
      'pages.MerchandisingCollections.pages.Collection.components.SlicesList.components.SliceCard',
  });

  const sliceStatus = getSliceStatus(slice);

  return (
    <Card
      key={slice.id}
      className="border-l-4"
      style={{ borderLeftColor: sliceStatus.color }}
    >
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <CardTitle className="text-lg">{slice.name}</CardTitle>

          <div className="flex items-center space-x-2">
            <Button asChild variant="outline" size="sm">
              <Link
                to={ROUTE_PATHS.MERCHANDISING_COLLECTIONS_SLICE.replace(
                  ':collectionId',
                  String(slice.collectionId),
                ).replace(':sliceId', String(slice.id))}
              >
                <Edit className="h-4 w-4" />
              </Link>
            </Button>

            <Button
              variant="outline"
              size="sm"
              className="text-destructive hover:text-destructive"
              onClick={() => onRemove(slice)}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-3">
        <div className="space-y-2 text-sm">
          <div className="text-muted-foreground">
            <span className="block">
              {slice.productsCount}{' '}
              {slice.productsCount !== 1
                ? t('productPlural')
                : t('productSingular')}
            </span>

            <span className="block">
              {t('startsAt')}{' '}
              <strong className="text-black">
                {format(slice.startsAt, "dd/MM/yyyy ' ' HH:mm")}
              </strong>
            </span>

            <span className="block">
              {t('endsAt')}{' '}
              <strong className="text-black">
                {format(slice.endsAt, "dd/MM/yyyy ' ' HH:mm")}
              </strong>
            </span>
          </div>

          <div className="grid grid-cols-1 gap-4 border-t pt-4 md:grid-cols-2">
            <div className="space-y-1">
              <p className="text-xs text-muted-foreground">{t('createdAt')}</p>
              <p className="text-sm">{format(slice.createdAt, 'dd/MM/yyyy')}</p>
            </div>

            <div className="space-y-1">
              <p className="text-xs text-muted-foreground">{t('updatedAt')}</p>
              <p className="text-sm">{format(slice.updatedAt, 'dd/MM/yyyy')}</p>
            </div>
          </div>
        </div>

        <SliceTags slice={slice} />
      </CardContent>
    </Card>
  );
}
